import { clarity } from 'react-microsoft-clarity'
import { apiClient } from '@/shared/api/apiClient'
import {
  type OperatorUserSession,
  type UserChangePasswordPayload,
} from '@/features/auth/auth-model'
import { CLARITY_USER_ID_STORAGE_KEY } from '@/shared/clarity-session-storage-keys'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { isAxiosError } from 'axios'

export const USER_SESSION_QUERY_KEY = 'session'

export type LoginPayload = {
  email: string
  password: string
}

export type SignUpPayload = {
  firstName: string
  lastName: string
  email: string
  password: string
}

export type SignUpWithInvitationPayload = SignUpPayload & {
  invitationCode: string
}

type RequestResetPasswordPayload = {
  email: string
}

type ResetPasswordPayload = {
  token: string
  password: string
}

type BoomError = {
  statusCode: number
  error: string
  message: string
}

export const errors = {
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  NOT_FOUND: 'NOT_FOUND',
  UNKOWN_ERROR: 'UNKNOWN_ERROR',
  INVALID_PASSWORD_RESET_TOKEN: 'INVALID_PASSWORD_RESET_TOKEN',
  PASSWORD_RESET_TOKEN_EXPIRED: 'PASSWORD_RESET_TOKEN_EXPIRED',
  PASSWORD_RESET_TOKEN_ALREADY_USED: 'PASSWORD_RESET_TOKEN_ALREADY_USED',
  INVITATION_EMAIL_MISMATCH: 'INVITATION_EMAIL_MISMATCH',
}

export const useGetUserSession = () => {
  return useQuery<OperatorUserSession | null>({
    queryKey: [USER_SESSION_QUERY_KEY],
    queryFn: async () => {
      try {
        const res = await apiClient.get<OperatorUserSession>('/v1/auth/session')

        if (clarity.hasStarted()) {
          const userId = String(res.data.id)
          sessionStorage.setItem(CLARITY_USER_ID_STORAGE_KEY, userId)
          clarity.identify(userId, {
            organizationId: res.data.organization.id,
          })
        }

        return res.data
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          sessionStorage.removeItem(CLARITY_USER_ID_STORAGE_KEY)
          return null
        }
        throw error
      }
    },
    retry: false,
  })
}

export const useLogin = () => {
  return useMutation({
    mutationFn: async (payload: LoginPayload) => {
      const res = await apiClient.post('/v1/auth/login', payload)
      return res.data
    },
  })
}

export const useSignUp = () => {
  return useMutation({
    mutationFn: async (payload: SignUpPayload) => {
      try {
        await apiClient.post('/v1/auth/signup', payload)

        return {
          success: true,
        }
      } catch (error) {
        if (axios.isAxiosError<BoomError>(error)) {
          if (error.response?.status === 409) {
            return {
              success: false,
              error: 'USER_ALREADY_EXISTS',
            }
          }
          return {
            success: false,
            error: error.response?.data.message || errors.UNKOWN_ERROR,
          }
        }

        return {
          success: false,
          error: error instanceof Error ? error.message : errors.UNKOWN_ERROR,
        }
      }
    },
  })
}

export const useLogout = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/v1/auth/session')
    },
    onSuccess: () => {
      sessionStorage.removeItem(CLARITY_USER_ID_STORAGE_KEY)
      queryClient.clear()
      window.location.reload()
    },
  })
}

export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (payload: UserChangePasswordPayload) => {
      const res = await apiClient.put('/v1/auth/password', payload)
      return res.data
    },
  })
}

export const useRequestResetPassword = () => {
  return useMutation({
    mutationFn: async (payload: RequestResetPasswordPayload) => {
      const res = await apiClient.post('/v1/auth/request-password-reset', payload)
      return res.data
    },
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (payload: ResetPasswordPayload) => {
      try {
        const res = await apiClient.post('/v1/auth/reset-password', payload)
        return res.data
      } catch (error) {
        if (axios.isAxiosError<BoomError>(error)) {
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'INVALID_PASSWORD_RESET_TOKEN'
          ) {
            throw new Error(errors.INVALID_PASSWORD_RESET_TOKEN)
          }
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'PASSWORD_RESET_TOKEN_EXPIRED'
          ) {
            throw new Error(errors.PASSWORD_RESET_TOKEN_EXPIRED)
          }
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'PASSWORD_RESET_TOKEN_ALREADY_USED'
          ) {
            throw new Error(errors.PASSWORD_RESET_TOKEN_ALREADY_USED)
          }
        }
        throw new Error(errors.UNKOWN_ERROR)
      }
    },
  })
}
